import { useMemo, useState, useRef, useEffect } from 'react';
import { useDisclosure, useToast } from '@chakra-ui/react';
import { SortingFn } from '@tanstack/react-table';
import { useGetKeywordsQuery } from 'core/API/keywordsApi';
import { ICategory, IkeywordsByType, IUserInfo } from 'types';
import { caseInsensitiveStringsOrder } from 'plugins/DITable/helpers/sort';
import { useTranslation } from 'helpers/languages/translations';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import {
    useGetKeywordCategoriesQuery,
    useLazyExportKeywordCategoriesQuery,
    useUpdateKeywordCategoriesMutation,
} from 'core/API/keywordCategoriesApi';
import { getCheckedKeywordsIds, getSelectedSegments } from 'pages/Keywords/helpers/dataGenerators';
import { updateKeywordsByType } from 'pages/Keywords/helpers/handlers';
import { EXCEL_FILE_TYPE } from 'shared/const';
import FileSaver from 'file-saver';
import { useGetRetailerByClientQuery } from 'core/API/authApi';

const useKeywordCategories = ({ readonly }: { readonly: boolean; userInfo?: IUserInfo }) => {
    const { data: keywords } = useGetKeywordsQuery({ type: 'search' });
    const [modalType, setModalType] = useState<'edit' | 'view'>('edit');
    const [exportKeywordCategories, { isLoading: isExportLoading, isFetching }] = useLazyExportKeywordCategoriesQuery();
    const [filters, setFilters] = useState<{ id: string; value: string }[]>([]);
    const [keywordFilter, setKeywordFilter] = useState<string>('');
    const [languages, setLanguages] = useState<string[]>([]);
    const toast = useToast();
    const { data: categoriesData, isError, isLoading } = useGetKeywordCategoriesQuery();
    const [updateCategoryKeywords, { isLoading: isUpdateCategoryLoading }] = useUpdateKeywordCategoriesMutation();
    const { data: retailers, isLoading: isRetailersLoading, isError: isRetailerError } = useGetRetailerByClientQuery();

    useEffect(() => {
        if (!isRetailersLoading && !isRetailerError && retailers) {
            const langs: Set<string> = new Set<string>();
            retailers.data.forEach((retailer) => {
                if (retailer.languages) {
                    retailer.languages.forEach((l: string) => {
                        if (l !== '') langs.add(l);
                    });
                }
            });

            const langsArr = Array.from(langs);
            setLanguages(langsArr);
        }
    }, [retailers, isRetailersLoading]);

    const { isOpen, onOpen, onClose } = useDisclosure();

    const translate = useTranslation();
    const updateInitialRef = useRef(null);
    const [data, setData] = useState<ICategory[]>([]);
    const [keywordsToSelect, setKeywordsToSelect] = useState({
        priorityKeyword: true,
        nonPriorityKeyword: true,
    });

    const [keywordsByType, setKeywordsByType] = useState<IkeywordsByType[]>([]);
    const [filteredKeywords, setFilteredKeywords] = useState<IkeywordsByType[]>([]);
    const [visibleKeywords, setVisibleKeywords] = useState<number[]>([]);
    const [treeOpenByDefault, setTreeOpenByDefault] = useState(false);

    const handleExportKeywordCategories = async () => {
        try {
            const { data: res, isError: isExportError } = await exportKeywordCategories({
                isTemplate: false,
            });
            if (isExportError || !res) throw new Error(translate('keyword.export.error') as string);

            const blob = new Blob([res], {
                type: EXCEL_FILE_TYPE,
            });
            FileSaver.saveAs(blob, translate('categories_keywords.export.file_name') as string);
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    useEffect(() => {
        const formattedKeywords: IkeywordsByType[] = [];
        if (keywords?.keywords) {
            keywords.keywords.forEach((keyword) => {
                const keywordType = keyword.keyword_type || 'default';

                const index = formattedKeywords.findIndex((formattedKeyword) => formattedKeyword.type === keywordType);
                if (index === -1) {
                    formattedKeywords.push({
                        id: keywordType,
                        type: keywordType,
                        name: keywordType,
                        checked: false,
                        keywords: [
                            {
                                id: String(keyword.keyword_id),
                                name: keyword.name,
                                type: keywordType,
                                master: keyword.master,
                                checked: false,
                                language: keyword.language,
                                index: 0,
                            },
                        ],
                    });
                } else {
                    formattedKeywords[index].keywords.push({
                        id: String(keyword.keyword_id),
                        name: keyword.name,
                        type: keywordType,
                        master: keyword.master,
                        language: keyword.language,
                        checked: false,
                        index: formattedKeywords[index].keywords.length,
                    });
                }
            });
        }
        setKeywordsByType(formattedKeywords);
    }, [keywords]);

    useEffect(() => {
        setFilteredKeywords(
            keywordsByType.map((type) => {
                return {
                    ...type,
                    keywords: type.keywords.filter((keyword) => {
                        if (keywordsToSelect.priorityKeyword && keywordsToSelect.nonPriorityKeyword) {
                            return keyword.name.toLowerCase().includes(keywordFilter);
                        }
                        if (keywordsToSelect.priorityKeyword) {
                            return keyword.master === 1 && keyword.name.toLowerCase().includes(keywordFilter);
                        }
                        if (keywordsToSelect.nonPriorityKeyword) {
                            return keyword.master === 0 && keyword.name.toLowerCase().includes(keywordFilter);
                        }
                        return true;
                    }),
                };
            }),
        );
    }, [keywordsByType, keywordFilter, keywordsToSelect]);

    useEffect(() => {
        if (!isLoading && !isError && categoriesData) {
            const formattedCategories: ICategory[] = [];
            categoriesData.keyword_categories.forEach((category) => {
                const level1Index = formattedCategories.findIndex((c) => c.id === category.level_1);
                if (level1Index === -1) {
                    formattedCategories.push({
                        id: category.level_1,
                        name: category.level_name_1,
                        level: 'category',
                        checked: false,
                        subRows: [
                            {
                                id: category.level_2,
                                name: category.level_name_2,
                                level: 'subcategory',
                                checked: false,
                                parentId: {
                                    id: category.level_1,
                                },
                                subRows: [
                                    {
                                        id: category.level_3,
                                        name: category.level_name_3,
                                        level: 'segment',
                                        keywords: category.keywords,
                                        parentId: {
                                            id: category.level_2,
                                            parentId: {
                                                id: category.level_1,
                                            },
                                        },
                                        subRows: [],
                                    },
                                ],
                            },
                        ],
                    });
                } else {
                    if (!formattedCategories[level1Index].subRows) formattedCategories[level1Index].subRows = [];
                    const level2Index = formattedCategories[level1Index].subRows.findIndex(
                        (subcategory) => subcategory.id === category.level_2,
                    );
                    if (level2Index === -1) {
                        formattedCategories[level1Index].subRows.push({
                            id: category.level_2,
                            name: category.level_name_2,
                            level: 'subcategory',
                            parentId: {
                                id: category.level_1,
                            },
                            subRows: [
                                {
                                    id: category.level_3,
                                    name: category.level_name_3,
                                    level: 'segment',
                                    checked: false,
                                    keywords: category.keywords,
                                    parentId: {
                                        id: category.level_2,
                                        parentId: {
                                            id: category.level_1,
                                        },
                                    },
                                    subRows: [],
                                },
                            ],
                        });
                    } else {
                        if (!formattedCategories[level1Index].subRows[level2Index].subRows)
                            formattedCategories[level1Index].subRows[level2Index].subRows = [];
                        const level3Index = formattedCategories[level1Index].subRows[level2Index].subRows.findIndex(
                            (segment) => segment.id === category.level_3,
                        );
                        if (level3Index === -1) {
                            formattedCategories[level1Index].subRows[level2Index].subRows.push({
                                id: category.level_3,
                                name: category.level_name_3,
                                level: 'segment',
                                keywords: category.keywords,
                                parentId: {
                                    id: category.level_2,
                                    parentId: {
                                        id: category.level_1,
                                    },
                                },
                                subRows: [],
                            });
                        }
                    }
                }
            });
            setData(formattedCategories);
        }
    }, [isLoading, isError, categoriesData]);

    const caseInsensitiveSort = useMemo(() => caseInsensitiveStringsOrder, []);
    const sortTypes: Record<string, SortingFn<any>> = useMemo(
        () => ({
            caseInsensitive: caseInsensitiveSort,
        }),
        [],
    );

    const handleEditCategories = () => {
        setKeywordFilter('');
        setModalType('edit');
        updateKeywordsByType(data, setKeywordsByType);
        onOpen();
    };

    const handleViewKeywords = (keywordList: number[]) => {
        setKeywordFilter('');
        setVisibleKeywords(keywordList);
        setModalType('view');
        onOpen();
    };

    const defaultColumn = useMemo(
        () => ({
            meta: { Filter: DefaultColumnFilter },
        }),
        [],
    );

    const handleCloseAddAndUpdateModal = () => {
        onClose();
    };

    const handleSubmit = async () => {
        const segmentIds = getSelectedSegments(data).map((e) => Number(e.id));

        try {
            const resp = await updateCategoryKeywords([
                {
                    ids: segmentIds,
                    level: 'segment',
                    keywords_ids: getCheckedKeywordsIds(keywordsByType),
                },
            ]);
            if ('error' in resp) {
                throw new Error();
            } else {
                toast({
                    title: translate('categories_keywords.update.success'),
                    status: 'success',
                    isClosable: true,
                });
            }
        } catch (e) {
            toast({
                title: translate('error.generic'),
                status: 'error',
                isClosable: true,
            });
        }

        handleCloseAddAndUpdateModal();
    };

    return {
        handleCloseAddAndUpdateModal,
        handleEditCategories,
        handleViewKeywords,
        keywords,
        modalType,
        setModalType,
        filters,
        setFilters,
        keywordFilter,
        setKeywordFilter,
        toast,
        categoriesData,
        isError,
        isLoading,
        updateCategoryKeywords,
        isUpdateCategoryLoading,
        isOpen,
        onOpen,
        onClose,
        treeOpenByDefault,
        setTreeOpenByDefault,
        translate,
        updateInitialRef,
        data,
        setData,
        keywordsToSelect,
        setKeywordsToSelect,
        keywordsByType,
        setKeywordsByType,
        filteredKeywords,
        setFilteredKeywords,
        visibleKeywords,
        setVisibleKeywords,
        handleSubmit,
        defaultColumn,
        sortTypes,
        exportKeywordCategories,
        isExportLoading,
        isFetching,
        handleExportKeywordCategories,
        languages,
        readonly,
    };
};

export type TKeywordCategories = ReturnType<typeof useKeywordCategories>;

export default useKeywordCategories;
